import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { SiteComplianceData } from '../../siteDetailTypes'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckIcon from '@mui/icons-material/Check'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

const Container = styled.div`
  padding: 1.5rem 0;
`

const Title = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const StatusCard = styled.div`
  background-color: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
`

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${palette.grey.grey100};
`

const StatusIconWrapper = styled.div<{ $status: string }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background-color: ${({ $status }) =>
    $status === 'Compliant'
      ? palette.success.green100
      : $status === 'Action Needed'
        ? palette.error.red100
        : palette.warning.yellow100};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StatusTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const StatusText = styled(Text)<{ $status: string }>`
  font-weight: 600;
  color: ${({ $status }) =>
    $status === 'Compliant'
      ? palette.success.green700
      : $status === 'Action Needed'
        ? palette.error.red700
        : palette.warning.yellow700};
`

const StatusDescription = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 0;
  border-bottom: 1px solid ${palette.grey.grey100};

  &:last-child {
    border-bottom: none;
  }
`

const ChecklistItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const ChecklistLabel = styled(Text)`
  font-size: 0.9375rem;
`

const DueDate = styled(Text)<{ $status?: 'warning' | 'error' }>`
  color: ${({ $status }) =>
    $status === 'warning' ? palette.warning.yellow700 : palette.error.red600};
  font-size: 0.875rem;
`

const RequirementsCard = styled.div`
  background-color: ${palette.grey.grey50};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 1.25rem;
`

const RequirementsTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const RequirementsList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
`

const RequirementItem = styled.li`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
  line-height: 1.75;
`

interface ComplianceTabProps {
  data: SiteComplianceData
  siteName: string
}

const ComplianceTab = ({ data, siteName }: ComplianceTabProps) => {
  return (
    <Container>
      <Title variant='body1'>Compliance Status</Title>

      <StatusCard>
        <StatusHeader>
          <StatusIconWrapper $status={data.status}>
            <CheckCircleIcon
              style={{
                fontSize: '1.5rem',
                color:
                  data.status === 'Compliant'
                    ? palette.success.green600
                    : data.status === 'Action Needed'
                      ? palette.error.red600
                      : palette.warning.yellow600,
              }}
            />
          </StatusIconWrapper>
          <StatusTextContainer>
            <StatusText $status={data.status}>{data.status}</StatusText>
            <StatusDescription>{data.statusDescription}</StatusDescription>
          </StatusTextContainer>
        </StatusHeader>

        {data.items.map((item) => (
          <ChecklistItem key={item.id}>
            <ChecklistItemLeft>
              {item.completed ? (
                <CheckIcon
                  style={{
                    fontSize: '1.25rem',
                    color: palette.success.green600,
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  style={{
                    fontSize: '1.25rem',
                    color:
                      item.status === 'warning'
                        ? palette.warning.yellow600
                        : palette.error.red500,
                  }}
                />
              )}
              <ChecklistLabel>{item.label}</ChecklistLabel>
            </ChecklistItemLeft>
            {item.dueDate && (
              <DueDate $status={item.status}>Due: {item.dueDate}</DueDate>
            )}
          </ChecklistItem>
        ))}
      </StatusCard>

      <RequirementsCard>
        <RequirementsTitle variant='body1'>
          {siteName} Requirements
        </RequirementsTitle>
        <RequirementsList>
          {data.requirements.map((req, index) => (
            <RequirementItem key={index}>{req.text}</RequirementItem>
          ))}
        </RequirementsList>
      </RequirementsCard>
    </Container>
  )
}

export default React.memo(ComplianceTab)
